import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { SurveyTemplate, TemplateCategory, TemplateListFilters, APIResponse } from '../types/survey.types';
import { retryFirebaseCall } from '../utils/retry';

interface UseTemplatesReturn {
  loading: boolean;
  error: string | null;
  listTemplates: (filters?: TemplateListFilters) => Promise<SurveyTemplate[]>;
  createTemplate: (template: Partial<SurveyTemplate>) => Promise<string>;
  saveAsTemplate: (surveyId: string, name: string, description?: string, category?: TemplateCategory, tags?: string[]) => Promise<string>;
  createSurveyFromTemplate: (templateId: string, title: string, description?: string, variables?: Record<string, string>) => Promise<string>;
  deleteTemplate: (templateId: string) => Promise<void>;
  updateTemplate: (templateId: string, updates: Partial<SurveyTemplate>) => Promise<void>;
  seedSystemTemplates: () => Promise<string[]>;
}

export const useTemplates = (): UseTemplatesReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listTemplates = useCallback(async (filters?: TemplateListFilters): Promise<SurveyTemplate[]> => {
    setLoading(true);
    setError(null);

    try {
      const listTemplatesFn = httpsCallable<
        { filters?: TemplateListFilters },
        APIResponse<SurveyTemplate[]>
      >(functions, 'listTemplates');

      const result = await retryFirebaseCall(
        () => listTemplatesFn({ filters }),
        'listTemplates'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to list templates');
      }

      return result.data.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to list templates';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const createTemplate = useCallback(async (template: Partial<SurveyTemplate>): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const createTemplateFn = httpsCallable<
        { template: Partial<SurveyTemplate> },
        APIResponse<string>
      >(functions, 'createTemplate');

      const result = await retryFirebaseCall(
        () => createTemplateFn({ template }),
        'createTemplate'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to create template');
      }

      return result.data.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create template';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveAsTemplate = useCallback(
    async (surveyId: string, name: string, description?: string, category?: TemplateCategory, tags?: string[]): Promise<string> => {
      setLoading(true);
      setError(null);

      try {
        const saveAsTemplateFn = httpsCallable<
          { surveyId: string; name: string; description?: string; category?: TemplateCategory; tags?: string[] },
          APIResponse<string>
        >(functions, 'saveAsTemplate');

        const result = await retryFirebaseCall(
          () => saveAsTemplateFn({ surveyId, name, description, category, tags }),
          'saveAsTemplate'
        );

        if (!result.data.success || !result.data.data) {
          throw new Error(result.data.error || 'Failed to save as template');
        }

        return result.data.data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to save as template';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createSurveyFromTemplate = useCallback(
    async (templateId: string, title: string, description?: string, variables?: Record<string, string>): Promise<string> => {
      setLoading(true);
      setError(null);

      try {
        const createSurveyFromTemplateFn = httpsCallable<
          { templateId: string; title: string; description?: string; variables?: Record<string, string> },
          APIResponse<string>
        >(functions, 'createSurveyFromTemplate');

        const result = await retryFirebaseCall(
          () => createSurveyFromTemplateFn({ templateId, title, description, variables }),
          'createSurveyFromTemplate'
        );

        if (!result.data.success || !result.data.data) {
          throw new Error(result.data.error || 'Failed to create survey from template');
        }

        return result.data.data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to create survey from template';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteTemplate = useCallback(async (templateId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const deleteTemplateFn = httpsCallable<
        { templateId: string },
        APIResponse<void>
      >(functions, 'deleteTemplate');

      const result = await retryFirebaseCall(
        () => deleteTemplateFn({ templateId }),
        'deleteTemplate'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to delete template');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete template';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTemplate = useCallback(
    async (templateId: string, updates: Partial<SurveyTemplate>): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const updateTemplateFn = httpsCallable<
          { templateId: string; updates: Partial<SurveyTemplate> },
          APIResponse<void>
        >(functions, 'updateTemplate');

        const result = await retryFirebaseCall(
          () => updateTemplateFn({ templateId, updates }),
          'updateTemplate'
        );

        if (!result.data.success) {
          throw new Error(result.data.error || 'Failed to update template');
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to update template';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const seedSystemTemplates = useCallback(async (): Promise<string[]> => {
    setLoading(true);
    setError(null);

    try {
      const seedFn = httpsCallable<
        Record<string, never>,
        APIResponse<string[]>
      >(functions, 'seedSystemTemplates');

      const result = await retryFirebaseCall(
        () => seedFn({}),
        'seedSystemTemplates'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to seed system templates');
      }

      return result.data.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to seed system templates';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    listTemplates,
    createTemplate,
    saveAsTemplate,
    createSurveyFromTemplate,
    deleteTemplate,
    updateTemplate,
    seedSystemTemplates,
  };
};
